import { DatePipe } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { RouterLink } from "@angular/router";
import { CommonModule } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime, distinctUntilChanged, first, take } from "rxjs";
import { Component, ViewEncapsulation, inject, input, output } from "@angular/core";

import { ButtonComponent } from "../button/button.component";
import { InputTextComponent } from "../input-text/input-text.component";
import { DatepickerComponent } from "../datepicker/datepicker.component";
import { FileViewerComponent } from "../file-viewer/file-viewer.component";
import { DeleteModalComponent } from "../delete-modal/delete-modal.component";

import { ExportService, TableOptions, TableService } from "src/app/core";

@Component({
  selector: "app-table",
  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    TranslateModule,
    TableModule,
    ReactiveFormsModule,
    DatePipe,
    RouterLink,
    ButtonComponent,
    InputTextComponent,
    DatepickerComponent,
  ],
  templateUrl: "./table.component.html",
  styleUrl: "./table.component.scss",
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe],
})
export class TableComponent {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private tableService = inject(TableService);
  private exportService = inject(ExportService);
  private translateService = inject(TranslateService);

  // Inputs
  readonly tableOptions = input.required<TableOptions>();
  readonly withPaginator = input(true);
  readonly clientSidePagination = input(false);

  // fields
  /* hold Columns Definitions */
  columns: TableOptions["inputCols"] = [];
  /* hold actions */
  actions: TableOptions["inputActions"] = [];
  /* hold the urls fro get and delete */
  url!: TableOptions["inputUrl"];
  /* hold bodyOptions */
  bodyOptions: TableOptions["bodyOptions"] = {
    pageNumber: 1,
    pageSize: 10,
  };

  /* hold the data */
  data: Record<string, unknown>[] = [];
  /* hold the total data */
  totalData: Record<string, unknown>[] = [];
  /* total count of records */
  totalCount = 0;
  /* hold the component name */
  componentName!: string | undefined;
  /* hold the permissions */
  permissions: string[] = [];
  /* hold the filter form */
  filterForm!: FormGroup;

  /* load data at first time */
  private firstInit!: boolean;

  readonly rows = output<any[]>();

  constructor(private datePipe: DatePipe) {
    this.columnSearchInput();
    // this.router.events
    //   .pipe(
    //     filter((event) => event instanceof NavigationEnd),
    //     takeUntilDestroyed(),
    //   )
    //   .subscribe((event: any) => {
    //     if (!event.url.includes("edit") && !event.url.includes("add")) this.loadDataFromServer();
    //   });
  }

  ngOnInit(): void {
    this.initializeInputs();
    this.initializeFilterForm();
    this.clientSidePagination() ? this.loadLazyLoadedData() : null;
  }

  initializeInputs(): void {
    // initialize url from config service
    this.url = this.tableOptions().inputUrl;

    // initialize columns
    const columns = this.tableOptions().inputCols.filter((col) => this.tableOptions().responsiveDisplayedProperties.includes(col.field));
    if (innerWidth <= 768) this.columns = columns;
    else this.columns = this.tableOptions().inputCols;
    this.actions = this.tableOptions().inputActions;
    const tableOptions = this.tableOptions();
    if (tableOptions.bodyOptions) this.bodyOptions = structuredClone(tableOptions.bodyOptions);
    this.permissions = tableOptions.permissions?.listOfPermissions || [];
    this.componentName = tableOptions.permissions?.componentName;
  }

  initializeFilterForm(): void {
    let filterFormControls: Record<string, any> = {};
    this.columns.forEach((col) => {
      if (col.filter) {
        if (col.filterColumnName) {
          if (col.filterColumnName.includes(",")) {
            const columns = col.filterColumnName.split(",");
            columns.forEach((column) => (filterFormControls[column] = [""]));
          } else filterFormControls[col.filterColumnName] = [""];
        } else filterFormControls[col.field] = [""];
      }
    });

    this.filterForm = this.fb.group(filterFormControls);
  }

  /* get search value when typing on column search box */
  columnSearchInput(): void {
    this.tableService.searchNew$.pipe(debounceTime(1000), distinctUntilChanged(), takeUntilDestroyed()).subscribe(() => {
      if (this.firstInit) {
        this.data = [];
        this.loadDataFromServer();
      } else this.firstInit = true;
    });
  }

  // load data from server
  loadDataFromServer(): void {
    if (this.clientSidePagination() && this.data.length) {
      // display next patch of data if exist
      const pagedData = this.totalData.slice(
        (this.bodyOptions.pageNumber - 1) * this.bodyOptions.pageSize,
        this.bodyOptions.pageNumber * this.bodyOptions.pageSize,
      );

      pagedData.length > 0 ? (this.data = pagedData) : null;
    } else {
      if (this.url.getAllMethod === "GET") {
        this.tableService.getData(this.url.getAll).subscribe((res) => ((this.data = res.data), this.rows.emit(this.data)));
      } else if (this.url.getAllMethod === "POST") {
        this.tableService.postData(this.url.getAll, this.bodyOptions).subscribe((res) => {
          this.data = res.data;
          this.rows.emit(res.data);

          if (this.clientSidePagination()) {
            this.totalData = res.data;
            this.totalCount = res.data.length;
          } else this.totalCount = res.totalCount;
        });
      }
    }
  }

  /* lazy load table data */
  /* note:  gets called on entering component */
  loadLazyLoadedData(event?: TableLazyLoadEvent): void {
    this.resetOpt();
    if (!this.bodyOptions.orderByValue?.length) this.setSortColumn(event);
    this.setPaging(event);
    this.loadDataFromServer();
  }

  // get cell data
  getCellData(row: any, property: any): any {
    let mainProperty, subProperty;

    if (property.includes(".")) {
      // Ex. task.name
      mainProperty = property.split(".").shift() as string;
      subProperty = property.split(".").pop() as string;

      // if (mainProperty.includes("[0]")) {
      //   // Ex. roles[0].name
      //   const arrayProperty = mainProperty.split("[0]").shift() as string;

      //   if (row[arrayProperty][0]) return row[arrayProperty][0][subProperty] ? row[arrayProperty][0][subProperty] : "-";
      //   else return "-";
      // }
      // make general if include [any number]
      if (mainProperty.includes("[")) {
        const arrayProperty = mainProperty.split("[").shift() as string;
        const arrayIndex = (mainProperty.split("[").pop() as string).split("]").shift() as string;
        if (row[arrayProperty][arrayIndex])
          return row[arrayProperty][arrayIndex][subProperty] ? row[arrayProperty][arrayIndex][subProperty] : "-";
        else return "-";
      }

      return row[mainProperty] ? (row[mainProperty][subProperty] ?? "-") : "-";
    } else {
      mainProperty = property;

      return row.hasOwnProperty(mainProperty) ? (row[mainProperty] ?? "-") : "-";
    }
  }

  /* set SortColumn */
  setSortColumn(event?: TableLazyLoadEvent): void {
    this.bodyOptions = {
      ...this.bodyOptions,
      orderByValue: [
        {
          colId: event?.sortField as string,
          sort: event?.sortOrder === 1 ? "asc" : "desc",
        },
      ],
    };
  }

  /* set paging parameters*/
  setPaging(event?: TableLazyLoadEvent): void {
    if (event?.rows) {
      this.bodyOptions = {
        ...this.bodyOptions,
        pageSize: (event?.rows as number) || 10,
        pageNumber: (event?.first as number) / (event?.rows as number) + 1 || 1,
      };
    }
  }

  /* reset server opt */
  resetOpt(): void {
    const tableOptions = this.tableOptions();
    if (tableOptions.bodyOptions) this.bodyOptions = tableOptions.bodyOptions;
    else {
      this.bodyOptions = {
        pageNumber: 1,
        pageSize: 10,
      };
    }
  }

  // Delete Modal
  openDeleteModal(row: any, call?: (row: any) => void): void {
    const modal = this.dialog.open(DeleteModalComponent, {
      panelClass: ["small", "p-0"],
    });

    modal
      .afterClosed()
      .pipe(first())
      .subscribe((dialogResult: boolean) => {
        if (dialogResult) {
          if (call) call(row);
          else this.deleteById(row.id);
        }
      });
  }

  // deleteByAppId
  deleteByAppId(id?: any): void {
    this.tableService
      .delete(this.url.delete as string, id)
      .pipe(take(1))
      .subscribe(() => this.loadDataFromServer());
  }

  // deleteById
  deleteById(id?: any): void {
    this.tableService
      .delete(this.url.delete as string, id)
      .pipe(take(1))
      .subscribe(() => this.loadDataFromServer());
  }

  // Filter
  filter(inputValue: any, column: any, filterColumnName: string, filterContent: string): void {
    this.resetOpt();
    inputValue = this.convertFilterContent(inputValue, filterContent);

    if (!this.bodyOptions?.filter) this.bodyOptions!.filter = {};

    if (filterColumnName !== undefined && filterColumnName !== "" && filterColumnName !== null) {
      if (filterColumnName.includes(",")) {
        const columns = filterColumnName.split(",");
        columns.forEach((column) => this.tableService.searchNew$.next((this.bodyOptions!.filter[column] = inputValue)));
      } else this.tableService.searchNew$.next((this.bodyOptions!.filter[filterColumnName] = inputValue));
    } else {
      this.tableService.searchNew$.next((this.bodyOptions!.filter[column] = inputValue));
    }
  }

  // convertFilterContent
  convertFilterContent(value: string, filterContent: string): string | number {
    if (filterContent === "number") return Number(value);

    return value;
  }

  get isFilterEnabled() {
    return this.columns.some((col) => col.filter);
  }

  getFinalRoute(actionRoute: string | undefined, id: string) {
    if (!actionRoute?.includes("{") && !actionRoute?.includes("}")) return `${actionRoute + id}`;

    if (actionRoute?.includes("{") && actionRoute?.includes("}")) return actionRoute.replace(/{id}/g, id);

    return "";
  }

  exportToExcel(sheetDetails: { [k: string]: string }, fileName: string) {
    this.exportService.excel(this.handleDateBeforeExporting(sheetDetails), fileName);
  }

  exportToPdf(sheetDetails: { [k: string]: string }, fileName: string, isEnglish: boolean, title?: string) {
    this.exportService.generatePdfFromData(this.handleDateBeforeExporting(sheetDetails), fileName, isEnglish, title);
  }

  handleDateBeforeExporting(sheetDetails: { [k: string]: string }) {
    const sheetColumnsValues = Object.keys(sheetDetails);
    const exportData = this.clientSidePagination() ? this.totalData : this.data;

    const newArray = exportData.map((eachData, index) => {
      let eachRow = {};

      sheetColumnsValues.map((eachColumnValue) => {
        eachRow = {
          ...eachRow,
          ...{ " ": index + 1 },
          [sheetDetails[eachColumnValue]]: eachColumnValue.toLowerCase().includes("date")
            ? this.datePipe.transform(eachData[eachColumnValue] as string, "d/M/yyyy")
            : eachData[eachColumnValue] || "-",
        };
      });

      return eachRow;
    });
    return newArray;
  }

  pdf(fileName: string): void {
    this.exportService.pdf(this.clientSidePagination() ? this.totalData : this.data, fileName);
  }

  onView(url: string, label: string) {
    const name = this.translateService.instant(label);
    const data = {
      url: url ?? "assets/images/empty/no_data.svg",
      nameEn: name,
      nameAr: name,
    };
    this.dialog.open(FileViewerComponent, {
      panelClass: ["default", "h-max-content", "p-0"],
      autoFocus: false,
      data: data,
    });
  }

  /* when leaving the component */
  ngOnDestroy() {
    this.tableService.searchNew$.next(null);
    this.resetOpt();
    this.firstInit = false;
  }
}
